import {showNotification} from 'platform/components';
import {RcFile} from 'rc-upload/lib/interface';

import {CSSProperties, useState} from 'react';

import {isNil} from 'ramda';

import {
  useSendMultipartUploadCompletedMutation,
  useSendMultipartUploadRequestMutation,
} from '@dms/api/base';
import {useAddFilesToVehicleAlbumMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {randomLowerCaseUUID, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  getWebChunk,
  isFileVideo,
  QueueVideoTask,
  RawFile,
  VideoUploadQueueProvider,
  useVideoUploadQueueContext,
} from 'features/video-upload-queue';

import {getVideoUploadErrorMessage} from '../../utils/getVideoUploadErrorMessage';
import {handleApiError} from '../../utils/handleApiError';
import {VehicleAlbumUploadVideoTile} from './VehicleAlbumUploadVideoTile';
import {VehicleAlbumVideoPlaceholder} from './VehicleAlbumVideoPlaceholder';

interface VehicleAlbumVideoUploadProps extends RequiredTestIdProps {
  vehicleId: string;
  vehicleAlbumId: string;
  ratio?: CSSProperties['aspectRatio'];
  isDisabled?: boolean;
  onUploaded?: () => void;
}

const VIDEO_TILE_RATIO = '4 / 3';
// `uploadProgress` reports 0–100; the ProgressBar expects a 0–1 fraction.
const PROGRESS_PERCENT_TO_FRACTION = 100;

// Provides a dedicated, chunked video-upload pipeline for a single vehicle album. Reuses the shared
// `VideoUploadQueueProvider` (multipart engine) and, once a file finishes uploading, attaches it to
// the album via `addFilesToVehicleAlbum` — the album counterpart of the audit-coupled
// `VideoUploadProvider` from the inspection flow.
export function VehicleAlbumVideoUpload(props: VehicleAlbumVideoUploadProps) {
  const [sendMultiPartUploadRequest] = useSendMultipartUploadRequestMutation();
  const [sendMultipartUploadCompleted] = useSendMultipartUploadCompletedMutation();

  return (
    <VideoUploadQueueProvider
      api={{sendMultipartUploadCompleted, sendMultiPartUploadRequest}}
      getChunk={getWebChunk}
      handleError={(errorMessage) =>
        showNotification.error(getVideoUploadErrorMessage(errorMessage))
      }
    >
      <VehicleAlbumVideoUploadManager {...props} />
    </VideoUploadQueueProvider>
  );
}

function VehicleAlbumVideoUploadManager(props: VehicleAlbumVideoUploadProps) {
  const {tasks, uploadProgress, handleMultiMultiPartUpload} = useVideoUploadQueueContext();
  const [addFilesToVehicleAlbum] = useAddFilesToVehicleAlbumMutation();
  // Files that finished chunk-upload and are being attached to the album (BE then generates
  // variants). Kept so the placeholder persists until the album list refetches.
  const [processingFileIds, setProcessingFileIds] = useState<string[]>([]);

  const ratio = props.ratio ?? VIDEO_TILE_RATIO;

  const stopProcessing = (fileId: string) =>
    setProcessingFileIds((ids) => ids.filter((id) => id !== fileId));

  const attachToAlbum = (fileId: string) => {
    addFilesToVehicleAlbum({
      vehicleId: props.vehicleId,
      vehicleAlbumId: props.vehicleAlbumId,
      addFilesToVehicleAlbumRequestBody: {fileIds: [fileId]},
    })
      .unwrap()
      .then(() => {
        props.onUploaded?.();
      })
      .catch(handleApiError)
      .finally(() => stopProcessing(fileId));
  };

  const handleUploadFinished = (task: QueueVideoTask) => {
    URL.revokeObjectURL(task.uri);
    setProcessingFileIds((ids) => [...ids, task.connectionFileId]);
    attachToAlbum(task.connectionFileId);
  };

  const handleStartVideoUpload = async (file: RcFile) => {
    if (!isFileVideo(file)) {
      showNotification.error(i18n.t('entity.inspection.errors.invalidFileType'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const rawFile: RawFile = {
      id: randomLowerCaseUUID(),
      uri: objectUrl,
      name: file.name,
      size: file.size,
      mimeType: file.type,
      connectionFileId: null,
    };

    const task = await handleMultiMultiPartUpload(rawFile, handleUploadFinished);
    if (isNil(task)) {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const getTaskProgress = (connectionFileId: string) =>
    (uploadProgress.find((progress) => progress.connectionFileId === connectionFileId)?.progress ??
      0) / PROGRESS_PERCENT_TO_FRACTION;

  const activeTasks = tasks.filter((task) => task.status !== 'ERROR');
  const activeTaskFileIds = new Set(activeTasks.map((task) => task.connectionFileId));
  const processingOnlyFileIds = processingFileIds.filter((id) => !activeTaskFileIds.has(id));

  return (
    <>
      {activeTasks.map((task) => (
        <VehicleAlbumVideoPlaceholder
          key={task.id}
          progress={getTaskProgress(task.connectionFileId)}
          ratio={ratio}
        />
      ))}
      {processingOnlyFileIds.map((fileId) => (
        <VehicleAlbumVideoPlaceholder key={fileId} ratio={ratio} />
      ))}
      <VehicleAlbumUploadVideoTile
        ratio={ratio}
        isDisabled={props.isDisabled}
        onStartVideoUpload={handleStartVideoUpload}
        data-testid={suffixTestId('vehicleAlbum-uploadVideo', props)}
      />
    </>
  );
}
